import { blinkConstants } from '@thoughtspot/blink-constants';
import { create } from '@thoughtspot/logger';
import { stringify } from 'flatted';
import _ from 'lodash';
import { updatePropertiesWithOverrides } from './event-property-overrides';
import { getEventSession } from './event-session-service';
import { init as initMixPanelPeopleProperties, PeopleSetPropertiesEnum, } from './mixpanel-people-properties';
import { hasRequiredMixpanelMethods, initService, isOptedOut, MixpanelCheckScope, notifyMixpanelDisabled, setMixpanelDNT, setOptInTracking, updateMixPanelProperty, uploadEventBatch, } from './mixpanel-service';
import { MixpanelSessionProperties, SessionPropsEnum, updatePropertiesWithEventName, } from './mixpanel-session-properties';
import { UserAction, userActionTypeEnum } from './user-action';
let mockEventList;
const Logger = create('event-collector');
let isEventCollectorOn;
const eventBatch = [];
let eventCollectorOpts;
let mixpanelOpts;
let mixpanelSessionProps;
const DEFAULT_THROTTLE_LIMIT = 600;
const THROTTLE_TIME = 60 * 60 * 1000;
const throttleLimits = { default: DEFAULT_THROTTLE_LIMIT };
const throttleInfoObj = {};
export const mixpanelNumPropsLimit = 200;
export const mixpanelPropSizeLimit = 255;
const preloadQueue = [];
const PRELOAD_QUEUE_LIMIT = 10;
function initThrottling() {
    const failureEvent = new UserAction(userActionTypeEnum.FAILURE_ALERT).name;
    const sendLogsEvent = new UserAction(userActionTypeEnum.SEND_LOGS_TO_SERVER)
        .name;
    throttleLimits[failureEvent] = 100;
    throttleLimits[sendLogsEvent] = 100;
    const time = Date.now();
    Object.keys(throttleLimits).forEach(eventType => {
        throttleInfoObj[eventType] = {
            tokenCount: throttleLimits[eventType],
            throttleCount: 0,
            timestamp: time,
        };
    });
}
function updateThrottling(eventType) {
    const curr_time = Date.now();
    const prev_incr_time = throttleInfoObj[eventType].timestamp;
    const event_limit = throttleLimits[eventType];
    const incremented_count = Math.min(((curr_time - prev_incr_time) * event_limit) / THROTTLE_TIME, event_limit);
    if (incremented_count > 0) {
        throttleInfoObj[eventType].timestamp = curr_time;
        throttleInfoObj[eventType].tokenCount += incremented_count;
    }
}
function sendThrottleEvent(throttledCount, eventType) {
    eventBatch.push({
        id: 'throttleEvent',
        props: {
            id: eventType,
            throttledCount,
        },
    });
}
function checkThrottle(eventType) {
    const throttleEventType = Object.keys(throttleInfoObj).includes(eventType)
        ? eventType
        : 'default';
    updateThrottling(throttleEventType);
    const curr_limit = throttleInfoObj[throttleEventType].tokenCount;
    if (curr_limit) {
        const throttleCount = throttleInfoObj[throttleEventType].throttleCount;
        if (throttleCount) {
            sendThrottleEvent(throttleCount, throttleEventType);
            throttleInfoObj[throttleEventType].throttleCount = 0;
        }
        throttleInfoObj[throttleEventType].tokenCount--;
        return true;
    }
    throttleInfoObj[throttleEventType].throttleCount++;
    return false;
}
function checkPropertyLimits(name, eventProps = {}) {
    if (Object.keys(eventProps).length > mixpanelNumPropsLimit) {
        Logger.warn(`Breached Mixpanel Checks, Count Props > ${mixpanelNumPropsLimit}. Event: ${name}.`);
    }
    Object.keys(eventProps).forEach(key => {
        const value = eventProps[key];
        if (_.isString(value) && _.size(value) > mixpanelPropSizeLimit) {
            Logger.warn(`Breached Mixpanel Checks, Prop Size > ${mixpanelPropSizeLimit}. Event: ${name} Prop: ${key}.`);
        }
        if (_.isArray(value)) {
            value.forEach((arrVal, index) => {
                if (_.isString(arrVal) &&
                    _.size(arrVal) > mixpanelPropSizeLimit) {
                    Logger.warn(`Breached Mixpanel Checks, Array Prop Size > ${mixpanelPropSizeLimit}. Event: ${name} Prop: ${key} Index: ${index}.`);
                }
            });
        }
    });
}
function stringifyJsonProps(eventProps = {}) {
    const updatedEventProps = {};
    Object.keys(eventProps).forEach(key => {
        const value = eventProps[key];
        if (_.isObject(value) && !Array.isArray(value)) {
            updatedEventProps[key] = stringify(value);
        }
        else {
            updatedEventProps[key] = eventProps[key];
        }
    });
    return updatedEventProps;
}
function pushEvent(name, props) {
    const stringifiedProps = stringifyJsonProps(props);
    const sessionGUIDprops = getEventSession();
    mixpanelSessionProps === null || mixpanelSessionProps === void 0 ? void 0 : mixpanelSessionProps.updateEventChangableSessionProperties();
    let pushProps = _.cloneDeep(stringifiedProps);
    Object.assign(pushProps, sessionGUIDprops, mixpanelSessionProps === null || mixpanelSessionProps === void 0 ? void 0 : mixpanelSessionProps.properties, stringifiedProps);
    pushProps = updatePropertiesWithOverrides(pushProps);
    if ((mixpanelOpts === null || mixpanelOpts === void 0 ? void 0 : mixpanelOpts.isE2E) === true || isEventCollectorOn) {
        checkPropertyLimits(name, pushProps);
        if (mockEventList) {
            mockEventList.push({ userAction: name, eventProps: pushProps });
        }
    }
    if (isEventCollectorOn) {
        if (checkThrottle(name)) {
            eventBatch.push({ id: name, props: pushProps });
        }
        if (Object.keys(eventBatch).length >=
            blinkConstants.eventCollector.EVENT_BATCH_THRESHOLD) {
            uploadEventBatch(eventBatch);
            eventBatch.length = 0;
        }
    }
}
export function collectEvent(userAction, eventProps = {}) {
    if (isEventCollectorOn === undefined &&
        preloadQueue.length <= PRELOAD_QUEUE_LIMIT) {
        preloadQueue.push({ userAction, eventProps });
    }
    const eventProperties = updatePropertiesWithEventName(eventProps, userAction.name);
    if (isEventCollectorOn) {
        try {
            updateMixPanelProperty(PeopleSetPropertiesEnum.lastPageVisited, mixpanelOpts.getPath());
        }
        catch (e) {
            Logger.info(e);
        }
    }
    pushEvent(String(eventProps.name), eventProperties);
}
export function collectWorkflowEvent(workflowProps) {
    const eventProperties = updatePropertiesWithEventName(workflowProps, workflowProps.name);
    pushEvent(String(workflowProps.name), eventProperties);
}
export function flushEvents() {
    if (eventBatch.length && isEventCollectorOn) {
        uploadEventBatch(eventBatch);
        eventBatch.length = 0;
    }
}
export async function initEventCollector(options) {
    if (!isEventCollectorOn) {
        return;
    }
    eventCollectorOpts = options;
    if (!eventCollectorOpts.isTrialVersionEnabled && isOptedOut()) {
        setMixpanelDNT(true);
        isEventCollectorOn = false;
        eventCollectorOpts.flagMixpanelDNT({ optedOut: 1 });
        Logger.warn('Mixpanel DNT is enabled');
        return;
    }
    if (eventCollectorOpts.isTrialVersionEnabled) {
        setOptInTracking();
    }
    setMixpanelDNT(false);
    if (mixpanelOpts.isE2E) {
        mixpanelSessionProps.updateSessionProperty(SessionPropsEnum.releaseVersion, 'e2eTest');
        mixpanelSessionProps.updateSessionProperty(SessionPropsEnum.blinkReleaseVersion, 'e2eTest');
        return;
    }
    const releaseVersion = eventCollectorOpts.releaseVersion;
    const loggedInProperties = {
        [SessionPropsEnum.userGUID]: options.userGUID,
        [SessionPropsEnum.maskedJsessionId]: options.partialJSessionGUID,
        [SessionPropsEnum.userLocale]: options.preferredUserLocale,
        [SessionPropsEnum.releaseVersion]: releaseVersion,
        [SessionPropsEnum.blinkReleaseVersion]: eventCollectorOpts.blinkReleaseVersion,
        [SessionPropsEnum.isAdminUser]: options.hasAdminPrivileges,
        [SessionPropsEnum.isSaaSCluster]: options.isSaaSCluster || false,
        [SessionPropsEnum.clusterId]: eventCollectorOpts.selfClusterId,
        [SessionPropsEnum.clusterName]: eventCollectorOpts.selfClusterName,
        [SessionPropsEnum.userPersona]: options.userPersona,
        [SessionPropsEnum.hasDataManagementPrivileges]: options.hasDataManagementPrivileges,
        [SessionPropsEnum.onboardingDataManagemenentFlag]: options.onboardingDataManagemenentFlag,
        [SessionPropsEnum.hasSeenOnboarding]: options.hasSeenOnboarding,
        [SessionPropsEnum.isAnswerV2Enabled]: options.isAnswerV2Enabled,
        [SessionPropsEnum.isPinboardV2Enabled]: options.isPinboardV2Enabled,
        [SessionPropsEnum.answerV2ExperienceEnabledOnMobile]: options.answerV2ExperienceEnabledOnMobile,
        [SessionPropsEnum.isDataPanelV2Experience]: options.isDataPanelV2Experience,
        [SessionPropsEnum.isConvAssistEnabled]: options.isConvAssistEnabled,
        [SessionPropsEnum.hasAIPrivilege]: options.hasAIPrivilege,
        [SessionPropsEnum.spotterPlanType]: options.spotterPlanType,
        [SessionPropsEnum.isDynamicParameterEnabled]: options.isDynamicParameterEnabled,
        [SessionPropsEnum.isConvexEnabled]: options.isConvexEnabled,
        [SessionPropsEnum.isSortV2Enabled]: options.isSortV2Enabled,
        [SessionPropsEnum.isChartSettingsV2Enabled]: options.isChartSettingsV2Enabled,
        [SessionPropsEnum.onboardingFlowSelected]: options.onboardingFlowSelected,
        [SessionPropsEnum.isCollectionsEnabled]: options.isCollectionsEnabled,
    };
    if (options.isEmbraceEnabled && options.getEmbraceTableStats) {
        try {
            const response = await options.getEmbraceTableStats(true);
            const numRows = response.numRows || 0;
            loggedInProperties[SessionPropsEnum.embraceRowCount] = numRows.toString();
        }
        catch (error) {
            loggedInProperties[SessionPropsEnum.embraceRowCount] = '0';
        }
    }
    if (options.isTeamEnabled) {
        loggedInProperties[SessionPropsEnum.trialExpiryDate] =
            options.trialExpiryDate;
        loggedInProperties[SessionPropsEnum.planType] = options.planType;
        loggedInProperties[SessionPropsEnum.teamId] = options.teamId;
    }
    loggedInProperties[SessionPropsEnum.isOrgEnabled] = options.isOrgEnabled;
    loggedInProperties[SessionPropsEnum.orgId] = options.orgId;
    mixpanelSessionProps.updateSessionProperties(loggedInProperties);
    await initMixPanelPeopleProperties({
        userGuid: options.userGUID,
        userEmail: options.userEmail,
        firstLoginDate: options.firstLoginDate,
        isFirstLogin: options.isFirstLogin,
        firstUtmSource: options.firstUtmSource,
        firstUtmMedium: options.firstUtmMedium,
        hasAdminPrivileges: options.hasAdminPrivileges,
        clusterId: [eventCollectorOpts.selfClusterId],
        clusterName: [eventCollectorOpts.selfClusterName],
        releaseVersion: [eventCollectorOpts.releaseVersion],
        blinkReleaseVersion: [options.blinkReleaseVersion],
        preferredLocale: [options.preferredUserLocale],
        getPath: mixpanelOpts.getPath,
        isEmbedded: [mixpanelOpts.isEmbedded],
        userPersona: options.userPersona,
        userPrivileges: [options.userPrivileges],
        teamOwner: options.teamOwner,
        isAnonymousUser: options.isAnonymousUser,
        userOrgs: options.userOrgs,
        teamId: options.teamId,
        isTeamsEnabled: !!options.isTeamEnabled,
    });
    preloadQueue.forEach(event => {
        collectEvent(event.userAction, event.eventProps);
    });
    preloadQueue.length = 0;
}
export function initMixpanel(opts) {
    mixpanelOpts = opts;
    mockEventList = opts.mockEventQueue;
    mixpanelSessionProps = new MixpanelSessionProperties(opts.isEmbedded, opts.isMobileEmbed || false, opts.getPath, opts.getContextState, opts.getCurrentPage, opts.getPreviousPage, opts.addEverywhereEventProps, opts.isLiveboardEnabled, opts.isOrgEnabled);
    const isMixpanelLibUsable = opts.mixpanelLib
        ? hasRequiredMixpanelMethods(opts.mixpanelLib, MixpanelCheckScope.CORE)
        : false;
    if (!opts.isMixpanelEnabled || !isMixpanelLibUsable) {
        Logger.warn(isMixpanelLibUsable
            ? 'Mixpanel is disabled via config, analytics disabled.'
            : 'Mixpanel lib not usable, analytics disabled.');
        isEventCollectorOn = false;
        notifyMixpanelDisabled();
        return;
    }
    initService(opts.mixpanelToken, opts.mixpanelLib, mixpanelSessionProps, opts.isDebugModeOn, opts.mixpanelApiHost, opts.enableCrossSubDomainCookie, opts.useLocalStorage, opts.enableABTesting, opts.enableSessionReplay, opts.teamId);
    if (!hasRequiredMixpanelMethods(opts.mixpanelLib, MixpanelCheckScope.PEOPLE)) {
        Logger.warn('Mixpanel lib missing people methods post-init, analytics disabled.');
        isEventCollectorOn = false;
        notifyMixpanelDisabled();
        return;
    }
    initThrottling();
    isEventCollectorOn = true;
}
export function clearArraySessionPropsForExperiments() {
    if (isEventCollectorOn) {
        mixpanelSessionProps.clearArraySessionProps(SessionPropsEnum.activeExperiments);
    }
}
export function setEventCollector(value) {
    isEventCollectorOn = value;
}
//# sourceMappingURL=event-collector.js.map